import { z } from 'zod';

import { slotIdSchema, WIDGET_LOCALE_RE } from './manifest';

export const WIDGET_PROTOCOL_VERSION = 1 as const;

// The query param the apps host puts on every widget document URL, carrying the
// PRODUCT origin that framed it.
//
// Both directions of the bridge require a pinned origin, and the frame has no
// other way to learn one: it cannot read `window.parent` across origins, and
// `document.referrer` depends on a policy the product page controls. So the value
// comes from the server, which derives it from the request Host through its
// trusted index — the same source the 401 page's `data-parent-origin` attribute
// uses, and never a string a client invented.
//
// Lives beside the message shapes because it belongs to the same frozen surface:
// lumi-mcp-server bakes it into `bundle_url` and every vendored copy of the SDK
// reads it.
export const WIDGET_PARENT_ORIGIN_PARAM = 'lumi_parent_origin';

const SchemaWidgetProtocolEnvelope = z
    .object({
        protocol: z.literal(WIDGET_PROTOCOL_VERSION),
    })
    .strict();

export type WidgetReadyMessage = z.infer<typeof SchemaWidgetReadyMessage>;
export const SchemaWidgetReadyMessage = SchemaWidgetProtocolEnvelope.extend({
    type: z.literal('widget.ready'),
});

// An in-PRODUCT path a widget may ask its host to navigate to.
//
// Deliberately a path and not a URL: the destination is always a page in the
// product that framed this widget, so an origin here could only ever be right by
// coincidence or wrong on purpose. The host resolves it against its own location.
//
// The grammar is what keeps a customer-authored bundle from steering the product
// somewhere else, so it is stricter than it first looks:
//
//   - must start with a single `/`     — `https://evil.test` and `mailto:` are out
//   - the second character is not `/`  — `//evil.test/x` is protocol-relative, and
//                                        resolving it would swap the origin
//   - no backslash anywhere            — some parsers fold `\` into `/`, so
//                                        `/\evil.test` is the same attack twice
//   - no whitespace or control chars   — a leading `\n` or space can hide a scheme
//                                        from a naive check
//
// Query and fragment are allowed; a widget linking to `/apps/foo?tab=bom` is
// ordinary. The host still routes through its own router, so the destination also
// faces whatever permission predicate that route declares.
export const WIDGET_PRODUCT_PATH_RE = /^\/(?![/\\])[^\s\\]*$/;

// A widget asking its host to navigate the product to one of its own pages.
//
// This exists because a widget CANNOT navigate on its own. Its iframe is
// sandboxed `allow-scripts allow-same-origin` — no `allow-popups`, no
// `allow-top-navigation` — so `target="_blank"` and `target="_top"` are both
// blocked, and a plain link would navigate the frame itself, loading the product
// inside a widget slot. Routing the intent through the host is what makes a link
// out of a widget possible at all, and it lands as a client-side route change
// rather than a full page load.
export type WidgetNavigateMessage = z.infer<typeof SchemaWidgetNavigateMessage>;
export const SchemaWidgetNavigateMessage = SchemaWidgetProtocolEnvelope.extend({
    type: z.literal('widget.navigate'),
    path: z.string().min(1).max(2000).regex(WIDGET_PRODUCT_PATH_RE),
    replace: z.boolean().optional(),
});

export const WIDGET_OPEN_URL_SCHEMES = ['https:', 'mailto:', 'tel:'] as const;

const isWidgetOpenUrl = (url: string): boolean => {
    try {
        return (WIDGET_OPEN_URL_SCHEMES as readonly string[]).includes(new URL(url).protocol);
    } catch {
        return false;
    }
};

export type WidgetOpenMessage = z.infer<typeof SchemaWidgetOpenMessage>;
export const SchemaWidgetOpenMessage = SchemaWidgetProtocolEnvelope.extend({
    type: z.literal('widget.open'),
    url: z.string().min(1).max(2000).refine(isWidgetOpenUrl),
});

export const WIDGET_NOTIFY_LEVELS = ['info', 'warning', 'error'] as const;
export type WidgetNotifyLevel = (typeof WIDGET_NOTIFY_LEVELS)[number];

export type WidgetNotifyMessage = z.infer<typeof SchemaWidgetNotifyMessage>;
export const SchemaWidgetNotifyMessage = SchemaWidgetProtocolEnvelope.extend({
    type: z.literal('widget.notify'),
    message: z.string().min(1).max(500),
    level: z.enum(WIDGET_NOTIFY_LEVELS),
});

export type WidgetBackMessage = z.infer<typeof SchemaWidgetBackMessage>;
export const SchemaWidgetBackMessage = SchemaWidgetProtocolEnvelope.extend({
    type: z.literal('widget.back'),
});

// The tallest content a widget may ask its slot to make room for.
//
// A ceiling exists because the number comes from a customer-authored bundle and the
// host turns it straight into a CSS height — a widget reporting a million pixels
// would hand the product a page nobody can scroll past, and a slot is not the place
// to notice that. Set far above any honest widget: past this, content belongs in a
// scroll container of the widget's own.
export const WIDGET_MAX_CONTENT_HEIGHT_PX = 20_000;

// A widget telling its host how tall its content is.
//
// An iframe cannot size itself. Its height is set by the embedder, who cannot read
// the layout of a cross-origin document, so the frame gets whatever the host
// guessed — leaving a short widget floating in dead space and a tall one behind an
// inner scrollbar the product never asked for. The measurement exists only INSIDE
// the frame, which makes the widget the only party who can supply it.
//
// `height` is the content height in CSS pixels; the host may round it up. Unlike
// `path` above, a number cannot express anywhere to go, so the bounds are sanity
// rather than boundary: strictly positive, since zero would mean "hide me" and that
// is not something a widget gets to ask for (the SDK holds its report back until
// there is something to measure), and no taller than the ceiling above. zod rejects
// NaN and Infinity on its own.
export type WidgetResizeMessage = z.infer<typeof SchemaWidgetResizeMessage>;
export const SchemaWidgetResizeMessage = SchemaWidgetProtocolEnvelope.extend({
    type: z.literal('widget.resize'),
    height: z.number().positive().max(WIDGET_MAX_CONTENT_HEIGHT_PX),
});

// The slot CONTEXT the host hands a widget once it reports ready.
//
// Includes the product bearer token so bundled LumiQuote clients can authenticate
// through their existing getToken()/setToken() integration. Delivery stays pinned
// to the assigned Apps origin and the exact iframe window.
const widgetLocaleSchema = z.string().min(2).max(35).regex(WIDGET_LOCALE_RE);

const slotTokenBaseFields = {
    type: z.literal('slot.token'),
    appId: z.string().min(1),
    params: z.record(z.string(), z.unknown()),
    // The absolute run-URL template from the assignments response
    // (`workflow_run_url_template`), passed through VERBATIM: the product never
    // parses or rebuilds it and the SDK only substitutes the `{workflowId}`
    // placeholder. The run route's shape is a server decision, so neither side may
    // assemble it.
    //
    // Optional because the response field is — a host with no derived apps origin
    // publishes neither this nor `bundle_url`. A widget without it has no workflow
    // surface, which (unlike a missing `bundle_url`) is not a reason to skip
    // rendering it.
    workflowRunUrlTemplate: z.string().min(1).optional(),
};

// What the HOST may emit: strict, so the product validates its own outgoing
// message against the exact field set and a typo or a stray field fails at the
// source rather than travelling.
export type SlotTokenMessage = z.infer<typeof SchemaSlotTokenMessage>;
export const SchemaSlotTokenMessage = SchemaWidgetProtocolEnvelope.extend({
    ...slotTokenBaseFields,
    // Kept for SDKs ≤0.4.0 vendored in shipped bundles: they REQUIRE `slotId` in
    // `slot.token` and reject the whole message without it. The SDK no longer
    // reads it (see `SchemaSlotTokenInbound`).
    slotId: slotIdSchema,
    token: z.string().min(1),
    tenant: z.string().min(1),
    locale: widgetLocaleSchema,
});

// What a WIDGET accepts: the same fields, but tolerant of keys it does not know,
// which zod then strips.
//
// Strict on both ends was a mistake, and an expensive one. The SDK is vendored
// into app bundles whose versions are immutable, so a strict receiver turns every
// later field addition into a break: shipped widgets don't ignore the new key,
// they reject the whole message and render nothing, forever. It also cuts the
// other way — a widget built against a newer SDK could not talk to a product
// release that still sent the older shape, which is exactly how the first
// SDK-built app met a host that was still sending `token`.
//
// So: strict where a message is authored, tolerant where one is consumed. The
// host cannot drift unnoticed (it validates what it sends), and the field set can
// grow without bricking anything already in the wild. What a widget refuses is
// still everything that matters — a wrong `protocol`, a missing `appId`, and (in
// the SDK) any sender whose origin isn't the pinned host.
//
// `slotId` is not read at all. The host still sends it (see above), but zod strips
// it here like any other key this schema does not name, so it never reaches widget
// code. The SDK only ever passed it through and no app reads it, while checking it
// against the `SLOT_IDS` enum, which is copied into every published SDK, rejected
// the whole `slot.token` for every framed slot added after that SDK was built. The
// widget does not need to know the slot to render in it: the host already picked it.
//
// `SchemaAppsSessionExpiredMessage` below already reasons this way for the same
// reason ("an unknown key must never be the reason session recovery stops
// working"). The only difference is that a vendored SDK cannot be redeployed to
// fix it later.
// Unknown keys are STRIPPED rather than passed through (zod's default for a plain
// object), so only fields explicitly supported by this SDK reach widget code.
export type SlotTokenInbound = z.infer<typeof SchemaSlotTokenInbound>;
export const SchemaSlotTokenInbound = z.object({
    protocol: z.literal(WIDGET_PROTOCOL_VERSION),
    ...slotTokenBaseFields,
    // Optional only for compatibility with host releases that predate these context
    // fields. New hosts always author both through the strict schema above.
    token: z.string().min(1).optional(),
    locale: widgetLocaleSchema.optional(),
    tenant: z.string().min(1).optional(),
});

// The product router's current location. The host owns routing and history; the
// widget receives this projection so it can render the route the host matched,
// including Back/Forward navigations it did not initiate itself. Router state is
// deliberately omitted because it may contain product-internal data and is not
// part of a URL's durable, deep-linkable identity.
const slotLocationFields = {
    type: z.literal('slot.location'),
    // Percent-encoded (`R%2025%25` for a board `R 25%`); older hosts send it decoded, so both pass.
    pathname: z
        .string()
        .min(1)
        .max(2000)
        .regex(/^\/(?![/\\])/),
    search: z.string().max(2000),
    hash: z.string().max(2000),
};

// Strict where the trusted host authors the message, tolerant where a vendored
// widget receives it. This mirrors slot.token's compatibility contract.
export type SlotLocationMessage = z.infer<typeof SchemaSlotLocationMessage>;
export const SchemaSlotLocationMessage = SchemaWidgetProtocolEnvelope.extend(slotLocationFields);

export type SlotLocationInbound = z.infer<typeof SchemaSlotLocationInbound>;
export const SchemaSlotLocationInbound = z.object({
    protocol: z.literal(WIDGET_PROTOCOL_VERSION),
    ...slotLocationFields,
});

export type WidgetLocation = Omit<SlotLocationMessage, 'protocol' | 'type'>;

// The apps host asking the product to re-mint its session.
//
// Sent by the tiny widget-session script on the 401 page the apps host serves in
// place of a widget document when the `__Host-apps_sess` cookie is missing or
// stale. Deliberately OUTSIDE the versioned widget protocol above: the sender is
// our own host page, not an app bundle, and it runs before any widget code has
// loaded — so it carries no protocol version and no payload beyond the type.
//
// The frame cannot recover on its own: /apps/_login lives on the product host
// and Auth0 refuses to be framed, and only the parent holds a product bearer. So
// the parent re-runs POST /apps/_session and reloads the frame that complained.
//
// Non-strict on purpose (unlike everything else here). The sender ships from
// another repo and may grow a diagnostic field; an unknown key must never be the
// reason session recovery stops working.
export const APPS_SESSION_EXPIRED_MESSAGE_TYPE = 'lumi:apps_session_expired' as const;

export type AppsSessionExpiredMessage = z.infer<typeof SchemaAppsSessionExpiredMessage>;
export const SchemaAppsSessionExpiredMessage = z.object({
    type: z.literal(APPS_SESSION_EXPIRED_MESSAGE_TYPE),
});

// Platform failures a widget can see but its host cannot. A CLOSED set of names,
// which is the whole point of the message: the host forwards these to its own
// Sentry, so a free-text field here would carry app-authored strings into a
// client running `sendDefaultPii: true`.
//
// `widget_slot_token_rejected` is the one that cannot be detected anywhere else.
// The host clears its ready deadline the moment it posts `slot.token` and never
// waits for an acknowledgement, so a widget whose inbound schema refuses that
// message leaves the parent believing the handshake succeeded while the user
// watches a widget stuck on its own loading state.
export const WIDGET_ERROR_SIGNALS = [
    'widget_slot_token_rejected',
    'widget_workflow_platform_failure',
    'widget_completion_platform_failure',
    'widget_sdk_internal_error',
] as const;
export type WidgetErrorSignal = (typeof WIDGET_ERROR_SIGNALS)[number];

const widgetErrorFields = {
    type: z.literal('widget.error'),
    signal: z.enum(WIDGET_ERROR_SIGNALS),
    // Which build of the SDK is reporting. A vendored bundle can be years older
    // than the host reading this, so the version is what turns "one app reports a
    // rejected token" into "every app pinned below X does".
    sdkVersion: z.string().min(1).max(64),
};

// What a WIDGET may emit: strict, so a signal name the SDK misspells fails in the
// widget rather than arriving as a mystery at the host.
export type WidgetErrorMessage = z.infer<typeof SchemaWidgetErrorMessage>;
export const SchemaWidgetErrorMessage = SchemaWidgetProtocolEnvelope.extend(widgetErrorFields);

// What the HOST accepts: tolerant, for the reason `SchemaSlotTokenInbound` is.
//
// `signal` is a bounded string rather than the enum above, and that is the
// difference that matters. A widget built against a newer SDK sends a name this
// host has never heard of; validating against today's enum would drop the message
// and hide the skew, so the host takes any short name and reports an unfamiliar
// one under a single bucket of its own. Length-capped because it becomes a tag.
export type WidgetErrorInbound = z.infer<typeof SchemaWidgetErrorInbound>;
export const SchemaWidgetErrorInbound = z.object({
    protocol: z.literal(WIDGET_PROTOCOL_VERSION),
    type: z.literal('widget.error'),
    signal: z.string().min(1).max(64),
    sdkVersion: z.string().min(1).max(64).optional(),
});

// Everything a widget may send its host. A discriminated union on `type`, so the
// host narrows to one member and zod reports against that member rather than
// unioning every error.
export type WidgetToSlotMessage = z.infer<typeof SchemaWidgetToSlotMessage>;
export const SchemaWidgetToSlotMessage = z.discriminatedUnion('type', [
    SchemaWidgetReadyMessage,
    SchemaWidgetNavigateMessage,
    SchemaWidgetOpenMessage,
    SchemaWidgetNotifyMessage,
    SchemaWidgetBackMessage,
    SchemaWidgetResizeMessage,
    SchemaWidgetErrorMessage,
]);

// Named for the RECEIVING side, so it is the tolerant one — the strict schema is
// for the host validating what it emits.
export type SlotToWidgetMessage = z.infer<typeof SchemaSlotToWidgetMessage>;
export const SchemaSlotToWidgetMessage = z.discriminatedUnion('type', [
    SchemaSlotTokenInbound,
    SchemaSlotLocationInbound,
]);

export type SlotToken = Omit<SlotTokenInbound, 'protocol' | 'type'>;
